import { useToast } from "@chakra-ui/react";
import { useEffect, useState } from "react";

import { BaseSubscriptionDto, OnlineSubscriptionDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";
import { $ok } from "foundation-ts/commons";
import { Nullable } from "foundation-ts/types";

import { defaultErrorToast, defaultSuccessToast } from "../utils/toast";

export const usePaymentStatus = (activeSubscription: Nullable<BaseSubscriptionDto>, onPaymentStarted: (msg: string) => void, onPaymentResolved: () => void) => {
    const toast = useToast();
    const [pendingAction, setPendingAction] = useState<Nullable<PaymentAction>>(null);
    const [snapshot, setSnapshot] = useState<Nullable<OnlineSubscriptionDto>>(null);

    useEffect(() => {
        if (!$ok(pendingAction)) { return; }

        const config = PAYMENT_CONFIGS[pendingAction!];
        if (!config.successCheck(snapshot, activeSubscription as OnlineSubscriptionDto)) { return; }

        toast(defaultSuccessToast(config.successMessage(activeSubscription! as OnlineSubscriptionDto)));
        onPaymentResolved();
        setPendingAction(null);
        setSnapshot(null);
    }, [activeSubscription, pendingAction]);

    const startPendingPayment = (action: PaymentAction) => {
        const config = PAYMENT_CONFIGS[action];
        setSnapshot(activeSubscription as OnlineSubscriptionDto);
        setPendingAction(action);
        onPaymentStarted(config.pendingMessage);
    }

    const handlePaymentError = (action: PaymentAction) => {
        onPaymentResolved();
        toast.close(`payment-toast-${action}`);
        toast(defaultErrorToast("Une erreur est survenue lors du paiement"));
    }

    return { startPendingPayment, handlePaymentError };
}

type PaymentAction = 'startSubscription' | 'buyPack' | 'upgradeToUnlimited' | 'buyCollaborators';

interface PaymentStatusConfig {
    action: PaymentAction;
    pendingMessage: string;
    successCheck: (prev: Nullable<OnlineSubscriptionDto>, next: Nullable<OnlineSubscriptionDto>) => boolean;
    successMessage: (data: OnlineSubscriptionDto) => string;
}

const PAYMENT_CONFIGS: Record<PaymentAction, PaymentStatusConfig> = {
    startSubscription: {
        action: 'startSubscription',
        pendingMessage: "Activation de votre abonnement en cours...",
        successCheck: (prev, next) => $ok(prev?.trialPeriod) && !$ok(next?.trialPeriod),
        successMessage: (data) => `${data.unlimited ? "Votre abonnement illimité a commencé" : `Votre abonnement standard a commencé, vous avez un crédit de ${data.signatureCount.subscriptionMax} signatures`}`
    },
    buyPack: {
        action: 'buyPack',
        pendingMessage: "Validation de votre achat d'un pack de signatures en cours...",
        successCheck: (prev, next) => $ok(next) && $ok(prev) && next!.signatureCount.packMax > prev!.signatureCount.packMax,
        successMessage: (data) => `Vous avez ${data.signatureCount.packMax - data.signatureCount.packConsumed} signatures supplémentaires disponibles`
    },
    upgradeToUnlimited: {
        action: 'upgradeToUnlimited',
        pendingMessage: "Activation de votre abonnement en illimité en cours...",
        successCheck: (prev, next) => !prev?.unlimited && !!next?.unlimited,
        successMessage: (_data) => "Votre abonnement illimité a commencé"
    },
    buyCollaborators: {
        action: 'buyCollaborators',
        pendingMessage: "Validation de votre achat de collaborateurs en cours...",
        successCheck: (prev, next) => $ok(next) && $ok(prev) && prev!.maxUsers < next!.maxUsers,
        successMessage: (_data) => `Vous pouvez, dès maintenant, ajouter vos nouveaux collaborateurs`
    }
}